import { msToTime } from './formatDate';

// severities used in mobile camera diagnostic log entries (GET /logs/mobile-camera/:examId) - kept in this
// module (not consts.js) since consts.js -> modals map -> MobileCameraLogModal -> this file would otherwise
// be a circular import, and MOBILE_LOG_SEVERITY is read at module scope below (TDZ error, not just unused-var)
export const MOBILE_LOG_SEVERITY = { ERROR: 'error', WARNING: 'warning', INFO: 'info' };

// theme only has main/light shades - warning.main (#ffc400) on white has ~1.6:1 contrast, unusable as text;
// shared here (not theme.js) since it's specific to this feature's warning markers/chips/tiles
export const WARNING_TEXT_COLOR = '#7A5C00';

// significant info-severity events that still deserve a marker on the timeline / a key moment
const SIGNIFICANT_INFO_EVENTS = ['rec.start', 'rec.stop', 'camera.init_success', 'mobile.duplicate_disconnected'];

const SEVERITY_RANK = { [MOBILE_LOG_SEVERITY.ERROR]: 3, [MOBILE_LOG_SEVERITY.WARNING]: 2, [MOBILE_LOG_SEVERITY.INFO]: 1 };

const isHeartbeat = (entry) => entry.category === 'heartbeat' || entry.evt === 'heartbeat';

// e.g. 5025000 -> '+01:23:45', used for the offset column and timeline tooltips
export const formatOffset = (offsetMs) => `+${msToTime(offsetMs, true)}`;

export const formatDurationMs = (intl, ms) => {
	// 'short' rather than 'narrow' because narrow shortens the cs 'min' to 'm'; short keeps the 'h'/'min'/'s'
	// abbreviations the design asks for. In en this renders as 'hr'/'sec', which is acceptable - the UI is
	// primarily Czech and translation.js only ships a cs locale.
	const unit = (value, unitName) => intl.formatNumber(value, { style: 'unit', unit: unitName, unitDisplay: 'short' });

	if (ms == null || Number.isNaN(ms) || ms < 0) return unit(0, 'second');

	const totalSeconds = Math.round(ms / 1000);
	const h = Math.floor(totalSeconds / 3600);
	const m = Math.floor((totalSeconds % 3600) / 60);
	const s = totalSeconds % 60;

	if (h > 0) return m > 0 ? `${unit(h, 'hour')} ${unit(m, 'minute')}` : unit(h, 'hour');
	if (m > 0) return s > 0 ? `${unit(m, 'minute')} ${unit(s, 'second')}` : unit(m, 'minute');
	return unit(s, 'second');
};

/**
 * Builds up/down intervals for a lane (connection / network / recording) out of a chronological entries array.
 * Only client-side events are used by default - server (`mobile.*`) events can lag behind the client clock,
 * so they're drawn as markers instead of being mixed into intervals (see design spec section 10).
 * The time before the first relevant event is left uncovered (unknown state) rather than guessed.
 */
const buildIntervals = (entries, { upEvts, downEvts, srcFilter = 'client' }) => {
	const relevant = entries.filter((e) => e.src === srcFilter && (upEvts.includes(e.evt) || downEvts.includes(e.evt)));
	const intervals = [];
	let current = null;

	for (const entry of relevant) {
		const state = upEvts.includes(entry.evt) ? 'up' : 'down';
		if (current?.state === state) continue; // e.g. a redundant reconnect while already up

		if (current) intervals.push({ start: current.start, end: entry.offsetMs, state: current.state, open: false });
		current = { start: entry.offsetMs, state };
	}

	if (current) intervals.push({ start: current.start, end: null, state: current.state, open: true });

	return intervals;
};

export const deriveIntervals = (entries) => ({
	socket: buildIntervals(entries, { upEvts: ['socket.connect', 'socket.reconnect'], downEvts: ['socket.disconnect'] }),
	net: buildIntervals(entries, { upEvts: ['net.online'], downEvts: ['net.offline'] }),
	rec: buildIntervals(entries, {
		upEvts: ['rec.start'],
		downEvts: ['rec.stop', 'rec.track_ended', 'rec.mediarecorder_stop_unexpected'],
	}),
});

const sumIntervalsMs = (intervals, totalDurationMs) =>
	intervals.reduce((sum, interval) => sum + ((interval.end ?? totalDurationMs) - interval.start), 0);

export const extractQueueDepthSeries = (entries) =>
	entries
		.filter((e) => isHeartbeat(e) && typeof e.data?.queueDepth === 'number')
		.map((e) => ({ offsetMs: e.offsetMs, value: e.data.queueDepth }));

export const buildMarkerEntries = (entries) =>
	entries.filter(
		(e) => !isHeartbeat(e) && (e.severity !== MOBILE_LOG_SEVERITY.INFO || SIGNIFICANT_INFO_EVENTS.includes(e.evt))
	);

/**
 * Buckets markers into groups at most `bucketPx` pixels apart on the (already time-scaled) x axis,
 * so the number of rendered SVG nodes is bounded by plotWidth/bucketPx instead of entries.length.
 */
export const clusterMarkers = (markers, plotWidth, durationMs, bucketPx = 12) => {
	if (!plotWidth || !durationMs) return [];

	const buckets = new Map();
	for (const marker of markers) {
		const x = (marker.offsetMs / durationMs) * plotWidth;
		const bucketIndex = Math.floor(x / bucketPx);
		if (!buckets.has(bucketIndex)) buckets.set(bucketIndex, []);
		buckets.get(bucketIndex).push(marker);
	}

	return Array.from(buckets.entries())
		.sort((a, b) => a[0] - b[0])
		.map(([bucketIndex, items]) => {
			const sorted = [...items].sort((a, b) => a.t - b.t);
			const severity = sorted.reduce(
				(worst, item) => (SEVERITY_RANK[item.severity] > SEVERITY_RANK[worst] ? item.severity : worst),
				MOBILE_LOG_SEVERITY.INFO
			);

			return { x: bucketIndex * bucketPx + bucketPx / 2, severity, count: sorted.length, items: sorted };
		});
};

/**
 * Textual "story" of the session for people who don't want to read the graph - max `maxCount` entries.
 * Simplified from the full spec algorithm: picks the first occurrence of the earliest milestones,
 * the first occurrence of every distinct non-info event type, every duplicate-connection event (always
 * a red flag) and the last event - rather than grouping consecutive problems into named clusters.
 */
export const buildMilestones = (entries, maxCount = 8) => {
	if (!entries?.length) return [];

	const milestones = [];
	const addFirst = (predicate) => {
		const found = entries.find(predicate);
		if (found && !milestones.includes(found)) milestones.push(found);
	};

	addFirst((e) => e.src === 'client' && e.evt === 'socket.connect');
	addFirst((e) => e.evt === 'camera.init_success' || e.evt === 'camera.init_fail');
	addFirst((e) => e.src === 'client' && e.evt === 'rec.start');

	const seenEvt = new Set();
	for (const entry of entries) {
		if (milestones.length >= maxCount - 1) break;
		if (entry.severity === MOBILE_LOG_SEVERITY.INFO || seenEvt.has(entry.evt)) continue;
		seenEvt.add(entry.evt);
		milestones.push(entry);
	}

	for (const entry of entries.filter((e) => e.evt === 'mobile.duplicate_disconnected')) {
		if (!milestones.includes(entry)) milestones.push(entry);
	}

	const last = entries[entries.length - 1];
	if (last && !milestones.includes(last)) milestones.push(last);

	return milestones.sort((a, b) => a.t - b.t).slice(0, maxCount);
};

/**
 * Collapses consecutive entries of the same evt/severity/category into one row with a repeat count
 * and a time range - typical for series of upload.fail or socket.reconnect_error. Assumes `entries`
 * is already the filtered/sorted list that will be rendered (called per render of the event list).
 */
export const mergeRepeated = (entries) => {
	const merged = [];

	for (const entry of entries) {
		const last = merged[merged.length - 1];
		if (last && last.evt === entry.evt && last.severity === entry.severity && last.category === entry.category) {
			last.count += 1;
			last.mergedEntries.push(entry);
			last.tEnd = entry.t;
		} else {
			merged.push({ ...entry, count: 1, mergedEntries: [entry], tEnd: entry.t });
		}
	}

	return merged;
};

const detailFromKeys = (data) =>
	Object.entries(data)
		.slice(0, 3)
		.map(([k, v]) => `${k}: ${typeof v === 'object' && v !== null ? JSON.stringify(v) : v}`)
		.join(', ');

// short human-readable extract of `data`, shown in the event list "Detail" column
// technical/numeric shorthand (key: value, no prose) so it doesn't need a translation per locale
export const formatLogDetail = (evt, data) => {
	if (!data || typeof data !== 'object') return '';

	switch (evt) {
		case 'rec.adaptive_bitrate':
			// mobile sends newBitrate in bps (RecordingService.ts), not kbps
			return data.newBitrate != null ? `→ ${Math.round(data.newBitrate / 1000)} kbps` : detailFromKeys(data);
		case 'heartbeat':
			return `queueDepth: ${data.queueDepth ?? '?'} · ${data.chunksUploaded ?? '?'}/${data.chunksProduced ?? '?'}`;
		default:
			return detailFromKeys(data);
	}
};

/**
 * Composes the one-sentence "black box" verdict. Returns structured data instead of a finished string
 * so the component can render it through react-intl messages (see LogSummary.js).
 */
export const buildVerdict = (summary, intervals, exam) => {
	const totalMs = summary?.durationMs ?? 0;
	const recIntervals = intervals?.rec ?? [];
	const netIntervals = intervals?.net ?? [];
	const socketIntervals = intervals?.socket ?? [];

	const hasRecording = recIntervals.length > 0;
	// buildIntervals returns both 'up' and 'down' spans - only 'up' is actual recording time
	const recordedMs = sumIntervalsMs(
		recIntervals.filter((i) => i.state === 'up'),
		totalMs
	);
	const offlineMs = sumIntervalsMs(
		netIntervals.filter((i) => i.state === 'down'),
		totalMs
	);

	const lastNet = netIntervals[netIntervals.length - 1];
	const lastSocket = socketIntervals[socketIntervals.length - 1];

	const endedOffline = lastNet?.state === 'down' && lastNet.open;
	const endedDisconnected = lastSocket?.state === 'down' && lastSocket.open;

	const eventCount = (evt) => summary?.eventCounts?.find((e) => e.evt === evt)?.count ?? 0;
	const uploadFails = eventCount('upload.fail');
	const crashCount = eventCount('rec.mediarecorder_error') + eventCount('rec.track_ended');

	let endsBeforeExamMinutes = null;
	if (exam?.endedAt && summary?.lastEntryAt) {
		const diffMs = new Date(exam.endedAt).getTime() - new Date(summary.lastEntryAt).getTime();
		if (diffMs > 5 * 60 * 1000) endsBeforeExamMinutes = Math.round(diffMs / 60000);
	}

	// The log starts when the mobile page opens, not when recording starts - there's a permission prompt,
	// socket handshake and examCreated in between, normally seconds but sometimes minutes if the candidate
	// gets stuck on the permission dialog. Measuring coverage against the whole log (totalMs) would flag that
	// normal startup gap as a recording problem, so the baseline is the window from the first rec.start instead.
	// A slow/failed start is still visible elsewhere: it's readable straight from the verdict's own numbers
	// ("Kamera nahrávala X z Y"), and a genuinely broken start already raises severity via error events
	// (camera.permission_denied / rec.start_fail).
	const firstRecUp = recIntervals.find((i) => i.state === 'up');
	const recWindowMs = firstRecUp ? totalMs - firstRecUp.start : 0;
	const uncoveredWindowMs = recWindowMs - recordedMs;
	const isRecordingMostlyCovered = recordedMs >= recWindowMs * 0.95;
	// a gap under a minute isn't actionable information for a reviewer (e.g. a short teardown tail at the end)
	const isGapWorthFlagging = uncoveredWindowMs > 60000;
	const hasUncoveredRecording = hasRecording && !isRecordingMostlyCovered && isGapWorthFlagging;

	let severity = 'success';
	if (hasUncoveredRecording || (summary?.severityCounts?.error ?? 0) > 0) severity = 'error';
	else if ((summary?.severityCounts?.warning ?? 0) > 0 || (summary?.disconnects ?? 0) > 0 || offlineMs > 0) {
		severity = 'warning';
	}

	return {
		severity,
		hasRecording,
		recordedMs,
		totalMs,
		disconnects: summary?.disconnects ?? 0,
		offlineMs,
		uploadFails,
		crashCount,
		endedOffline,
		endedDisconnected,
		endsBeforeExamMinutes,
	};
};
